import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import { comparePrompts, researchSources } from '../../data/researchDemo';
import type { ResearchSource } from '../../types/researchDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { glassPanel, linkButton } from '../../utils/demoUi';
import { sourceById } from '../../utils/researchDemo';
const rows: {
  label: string;
  render: (source: ResearchSource) => React.ReactNode;
}[] = [{
  label: 'Discipline',
  render: source => source.discipline
}, {
  label: 'Study design',
  render: source => source.design
}, {
  label: 'Population',
  render: source => source.population
}, {
  label: 'Method',
  render: source => source.method
}, {
  label: 'Finding',
  render: source => source.finding
}, {
  label: 'Interpretation limits',
  render: source => source.limit
}, {
  label: 'Supporting passages',
  render: source => <ul className="space-y-2" data-id="element-2036" data-mp="rxwpdpz0id70">
        {source.passages.map(passage => <li key={passage.id} className="border-l-2 border-navy/30 pl-3 font-serif leading-relaxed" data-id="element-2037" data-mp="s014l42d2yml">
            <span className="font-mono text-xs font-semibold text-ink-muted" data-id="element-2038" data-mp="n0xwfar34cae">{passage.id} </span>“{passage.text}”
          </li>)}
      </ul>
}];
export function CompareStudies() {
  const {
    state,
    dispatch,
    inspectSource
  } = useResearchDemo();
  const selected = state.ui.compareIds.map(sourceById).filter((source): source is ResearchSource => Boolean(source));
  const full = selected.length >= 4;
  return <div data-id="element-2039" data-mp="hzgxhks5uzwg">
      <DemoSectionHeader title="Compare Studies" description="Place two to four studies side by side. Different designs, populations and outcome measures are shown as context, not labeled as contradictions." data-id="element-2040" data-mp="i0a8wz22edkx" />

      <fieldset className={`${glassPanel} p-5`} data-id="element-2041" data-mp="3lnivmcdhpbyg">
        <legend className="float-left mb-3 w-full text-sm font-semibold text-ink-dark" data-id="element-2042" data-mp="3gpqucrqygdhl">
          Studies to compare <span className="font-normal text-ink-muted" data-id="element-2043" data-mp="1kmvtp1wps8eb">({selected.length} of up to 4 selected)</span>
        </legend>
        <div className="clear-both grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-2044" data-mp="1pkxcrmu547ps">
          {researchSources.map(source => {
          const checked = state.ui.compareIds.includes(source.id);
          const disabled = !checked && full;
          return <label key={source.id} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors duration-150 ${checked ? 'border-lavender/40 bg-lavender-pale' : 'border-line bg-white'} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`} data-id="element-2045" data-mp="1zdbh8nclrsfn">
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => dispatch({
              type: 'toggleCompare',
              id: source.id
            })} className="mt-0.5 h-4 w-4 accent-[#6366C9]" data-id="element-2046" data-mp="1fp2pgeez1ogo" />
                <span data-id="element-2047" data-mp="fetc2a84wlzy">
                  <span className="font-mono text-xs font-semibold text-ink-muted" data-id="element-2048" data-mp="fhflkfel5bkt">{source.id}</span>{' '}
                  <span className="font-semibold text-ink-dark" data-id="element-2049" data-mp="bg3qhucxzy58">{source.title}</span>
                </span>
              </label>;
        })}
        </div>
      </fieldset>

      {selected.length < 2 ? <div className={`${glassPanel} mt-6 p-10 text-center`} data-id="element-2050" data-mp="3stzen7lij0ij">
          <p className="font-heading font-bold text-ink-dark" data-id="element-2051" data-mp="3sviwh2mthjmf">Select at least two studies</p>
          <p className="mt-2 text-sm text-ink-muted" data-id="element-2052" data-mp="3ntthjbwh4lt2">The comparison table appears once two studies are selected.</p>
        </div> : <div className={`${glassPanel} mt-6 overflow-x-auto`} data-id="element-2053" data-mp="3eupww3hnrycg">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm" data-id="element-2054" data-mp="3ewaw46ex45gn">
            <caption className="sr-only" data-id="element-2055" data-mp="1i1o5q093wl3w">Side-by-side comparison of selected studies</caption>
            <thead data-id="element-2056" data-mp="1d3mm9e7rqjrw">
              <tr className="border-b border-line" data-id="element-2057" data-mp="1d10epekvo49o">
                <th scope="col" className="w-40 px-5 py-4 text-xs font-semibold text-ink-muted" data-id="element-2058" data-mp="9rxqaz9wc4kn">
                  Study
                </th>
                {selected.map(source => <th key={source.id} scope="col" className="px-5 py-4 align-top" data-id="element-2059" data-mp="s0wykckahj9">
                    <span className="font-mono text-xs font-semibold text-ink-muted" data-id="element-2060" data-mp="5qc4upjaqhyu">{source.id}</span>
                    <span className="mt-1 block font-heading text-base font-bold text-ink-dark" data-id="element-2061" data-mp="3n7c47k3r1gcb">{source.title}</span>
                    <button type="button" onClick={() => inspectSource(source.id, source.passages[0].id)} className={`mt-2 ${linkButton}`} data-id="element-2062" data-mp="3ry29kxc6dj70">
                      Read passages
                      <ArrowRight size={14} aria-hidden="true" data-id="element-2063" data-mp="3i7psjf3kk85m" />
                      <span className="sr-only" data-id="element-2064" data-mp="3i88ydxfy363a"> from {source.id}</span>
                    </button>
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-2065" data-mp="395zqzgfz3su4">
              {rows.map(row => <tr key={row.label} className="border-b border-line last:border-b-0" data-id="element-2066" data-mp="2pl996vw4dkdb">
                  <th scope="row" className="px-5 py-4 align-top text-xs font-semibold text-ink-muted" data-id="element-2067" data-mp="2kmfux190dpc3">
                    {row.label}
                  </th>
                  {selected.map(source => <td key={source.id} className="px-5 py-4 align-top leading-relaxed text-ink-dark" data-id="element-2068" data-mp="2uklyroedl5jl">
                      {row.render(source)}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>}

      <section aria-labelledby="compare-prompts" className="mt-8 rounded-2xl border border-line bg-white/80 p-6" data-id="element-2069" data-mp="33jfyhnn06c1d">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-2070" data-mp="3dbdct39b5k4b">
          <h2 id="compare-prompts" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2071" data-mp="3ddg0hgmzn15v">
            Questions for investigation
          </h2>
          <p className="text-xs font-medium text-ink-muted" data-id="element-2072" data-mp="3na2mw9uyq0a0">Demonstration prompts · fixed text, not live AI-generated analysis</p>
        </div>
        <ul className="mt-4 space-y-2" data-id="element-2073" data-mp="3uad5t5umljrt">
          {comparePrompts.map(prompt => <li key={prompt} className="flex gap-3 leading-relaxed text-ink-dark" data-id="element-2074" data-mp="36g3ptnf2fdb">
              <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lavender" data-id="element-2075" data-mp="84hnyhwkwkqd" />
              {prompt}
            </li>)}
        </ul>
      </section>
    </div>;
}